import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Search, SlidersHorizontal, Columns3, X, Check, ChevronDown, Package, Layers, Beaker, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export type Level = 1 | 2 | 3;
export interface FilterState {
  search: string;
  dateFrom: string;
  dateTo: string;
  status: 'Alle' | 'Abgeschlossen' | 'In Produktion' | 'Bereit zum Verpacken';
  searchFields: string[];
}
export interface ColumnDef {
  key: string;
  label: string;
  required?: boolean;
}
interface FilterBarProps {
  level: Level;
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  columns?: ColumnDef[];
  visibleColumns?: string[];
  onVisibleColumnsChange?: (cols: string[]) => void;
  /** Distinct values per field for the autocomplete suggestions. Key = field key (e.g. "Lieferant"). */
  suggestions?: Record<string, string[]>;
  /** Optional CTA to open the bulk transfer modal (only meaningful on Chargen level). */
  onOpenTransfer?: () => void;
  /** Number of pending (non-transferred) charges shown as a badge on the transfer button. */
  pendingTransferCount?: number;
}
const LEVEL_META: Record<Level, {
  title: string;
  placeholder: string;
  icon: typeof Package;
  showDates: boolean;
  showStatus: boolean;
}> = {
  1: {
    title: 'Produkte',
    placeholder: 'Suchen oder auswählen…',
    icon: Package,
    showDates: false,
    showStatus: false
  },
  2: {
    title: 'Chargen',
    placeholder: 'Suchen oder auswählen…',
    icon: Layers,
    showDates: true,
    showStatus: true
  },
  3: {
    title: 'Rohstoffe',
    placeholder: 'Suchen oder auswählen…',
    icon: Beaker,
    showDates: false,
    showStatus: false
  }
};
export const LEVEL_FIELDS: Record<Level, {
  key: string;
  label: string;
}[]> = {
  1: [{
    key: 'Produktname',
    label: 'Produktname'
  }, {
    key: 'Produktnummer',
    label: 'Produktnummer'
  }],
  2: [{
    key: 'Chargen-Nr.',
    label: 'Chargen-Nr.'
  }, {
    key: 'Produktionsdatum',
    label: 'Produktionsdatum'
  }],
  3: [{
    key: 'Material',
    label: 'Material'
  }, {
    key: 'Lieferant',
    label: 'Lieferant'
  }, {
    key: 'Chargen-Nr.',
    label: 'Chargen-Nr.'
  }, {
    key: 'MHD',
    label: 'MHD'
  }]
};
export function FilterBar({
  level,
  filters,
  onChange,
  columns = [],
  visibleColumns = [],
  onVisibleColumnsChange,
  suggestions = {},
  onOpenTransfer,
  pendingTransferCount = 0
}: FilterBarProps) {
  const meta = LEVEL_META[level];
  const Icon = meta.icon;
  const allFields = LEVEL_FIELDS[level];
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const fieldsRef = useRef<HTMLDivElement>(null);
  useClickOutside(fieldsRef, () => setFieldsOpen(false));
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useClickOutside(searchWrapRef, () => setSuggestOpen(false));
  const update = (patch: Partial<FilterState>) => onChange({
    ...filters,
    ...patch
  });
  const activeFilterCount = (filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0) + (filters.status !== 'Alle' ? 1 : 0);
  const hiddenColumnsCount = columns.length ? columns.filter(c => !c.required && !visibleColumns.includes(c.key)).length : 0;
  const selectedFieldCount = filters.searchFields.length;
  const allSelected = selectedFieldCount === allFields.length;
  const toggleField = (key: string) => {
    if (filters.searchFields.includes(key)) {
      const next = filters.searchFields.filter(k => k !== key);
      update({
        searchFields: next.length === 0 ? allFields.map(f => f.key) : next
      });
    } else {
      update({
        searchFields: [...filters.searchFields, key]
      });
    }
  };
  const fieldsSummary = allSelected ? 'Alle Felder' : selectedFieldCount === 1 ? allFields.find(f => f.key === filters.searchFields[0])?.label ?? '1 Feld' : `${selectedFieldCount} Felder`;
  // Build flat suggestion list across selected fields, filtered by current term
  type SuggestionItem = {
    value: string;
    field: string;
  };
  const filteredSuggestions = useMemo<SuggestionItem[]>(() => {
    const term = filters.search.trim().toLowerCase();
    const seen = new Set<string>();
    const items: SuggestionItem[] = [];
    for (const fieldKey of filters.searchFields) {
      const values = suggestions[fieldKey] || [];
      for (const v of values) {
        if (!v) continue;
        const key = `${fieldKey}::${v}`;
        if (seen.has(key)) continue;
        if (term && !v.toLowerCase().includes(term)) continue;
        seen.add(key);
        items.push({
          value: v,
          field: fieldKey
        });
        if (items.length >= 50) break;
      }
      if (items.length >= 50) break;
    }
    return items;
  }, [filters.search, filters.searchFields, suggestions]);
  // Reset active suggestion index when list changes
  useEffect(() => {
    setActiveSuggestion(0);
  }, [filteredSuggestions.length, suggestOpen]);
  const pickSuggestion = (item: SuggestionItem) => {
    update({
      search: item.value
    });
    setSuggestOpen(false);
    inputRef.current?.blur();
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestOpen || filteredSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(i => (i + 1) % filteredSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(i => (i - 1 + filteredSuggestions.length) % filteredSuggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pickSuggestion(filteredSuggestions[activeSuggestion]);
    } else if (e.key === 'Escape') {
      setSuggestOpen(false);
    }
  };
  return <div className="sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-gray-200 px-4 sm:px-6 lg:px-10 2xl:px-16 py-3 mb-6 shadow-sm" data-id="element-241">
      <div className="w-full flex flex-wrap items-center gap-3" data-id="element-242">
        {/* Context label */}
        <div className="flex items-center gap-2 pr-3 mr-1 border-r border-gray-200" data-id="element-243">
          <div className="w-7 h-7 rounded-md bg-[#E87722]/10 flex items-center justify-center" data-id="element-244">
            <Icon className="w-3.5 h-3.5 text-[#E87722]" strokeWidth={2.5} data-id="element-245" />
          </div>
          <span className="text-sm font-semibold text-gray-700" data-id="element-246">
            {meta.title} filtern
          </span>
        </div>

        {/* Unified search with field-scope picker + suggestions */}
        <div ref={searchWrapRef} className="relative flex-1 min-w-[280px] max-w-2xl" data-id="element-247">
          <div className="flex items-stretch border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-[#E87722]/30 focus-within:border-[#E87722] transition-all" data-id="element-248">
            <div className="flex items-center pl-3 pr-2 text-gray-400" data-id="element-249">
              <Search className="h-4 w-4" data-id="element-250" />
            </div>
            <input ref={inputRef} type="text" value={filters.search} onChange={e => {
            update({
              search: e.target.value
            });
            setSuggestOpen(true);
          }} onFocus={() => setSuggestOpen(true)} onKeyDown={onKeyDown} placeholder={meta.placeholder} className="flex-1 py-2 pr-2 text-sm bg-transparent placeholder-gray-400 focus:outline-none" data-id="element-251" />
            {filters.search && <button onClick={() => {
            update({
              search: ''
            });
            inputRef.current?.focus();
          }} className="px-2 text-gray-400 hover:text-gray-600 flex items-center" aria-label="Suche löschen" data-id="element-252">
                <X className="w-3.5 h-3.5" data-id="element-253" />
              </button>}

            {/* Field scope picker */}
            <div ref={fieldsRef} className="relative border-l border-gray-200" data-id="element-254">
              <button onClick={() => setFieldsOpen(o => !o)} className={`h-full px-3 flex items-center gap-1.5 text-xs font-medium transition-colors ${fieldsOpen ? 'bg-[#E87722]/10 text-[#E87722]' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-255">
                <span className="text-gray-400" data-id="element-256">in:</span>
                <span className="max-w-[120px] truncate" data-id="element-257">{fieldsSummary}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${fieldsOpen ? 'rotate-180' : ''}`} data-id="element-258" />
              </button>

              <AnimatePresence data-id="element-259">
                {fieldsOpen && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.15
              }} className="absolute right-0 mt-2 w-60 bg-white rounded-xl border border-gray-200 shadow-xl z-40 overflow-hidden" data-id="element-260">
                    <div className="px-4 py-2.5 border-b border-gray-100" data-id="element-261">
                      <h4 className="text-xs font-semibold text-gray-900" data-id="element-262">
                        Suchen in
                      </h4>
                      <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-263">
                        Felder für die Suche auswählen
                      </p>
                    </div>
                    <div className="py-1 max-h-72 overflow-y-auto" data-id="element-264">
                      {allFields.map(field => {
                    const checked = filters.searchFields.includes(field.key);
                    return <button key={field.key} onClick={() => toggleField(field.key)} className="w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 transition-colors" data-id="element-265">
                            <span className="text-gray-700" data-id="element-266">{field.label}</span>
                            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${checked ? 'bg-[#E87722] border-[#E87722]' : 'border-gray-300 bg-white'}`} data-id="element-267">
                              {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-268" />}
                            </div>
                          </button>;
                  })}
                    </div>
                    <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex justify-between" data-id="element-269">
                      <button onClick={() => update({
                    searchFields: allFields.map(f => f.key)
                  })} className="text-xs text-gray-600 hover:text-[#E87722] font-medium" data-id="element-270">
                        Alle auswählen
                      </button>
                      <button onClick={() => setFieldsOpen(false)} className="text-xs text-[#E87722] font-medium" data-id="element-271">
                        Fertig
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Value suggestions dropdown */}
          <AnimatePresence data-id="element-272">
            {suggestOpen && filteredSuggestions.length > 0 && <motion.div initial={{
            opacity: 0,
            y: -4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.12
          }} className="absolute left-0 right-0 mt-1.5 bg-white rounded-xl border border-gray-200 shadow-xl z-30 overflow-hidden" data-id="element-273">
                <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between bg-gray-50/50" data-id="element-274">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-275">
                    Vorschläge
                  </span>
                  <span className="text-[11px] text-gray-400" data-id="element-276">
                    {filteredSuggestions.length} Treffer
                  </span>
                </div>
                <ul className="max-h-72 overflow-y-auto py-1" role="listbox" data-id="element-277">
                  {filteredSuggestions.map((s, i) => {
                const term = filters.search.trim();
                return <li key={`${s.field}-${s.value}-${i}`} role="option" aria-selected={i === activeSuggestion} data-id="element-278">
                        <button onMouseDown={e => {
                    e.preventDefault();
                    pickSuggestion(s);
                  }} onMouseEnter={() => setActiveSuggestion(i)} className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors ${i === activeSuggestion ? 'bg-[#E87722]/10 text-[#E87722]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-279">
                          <span className="truncate" data-id="element-280">
                            <HighlightMatch text={s.value} term={term} data-id="element-281" />
                          </span>
                          <span className={`ml-3 shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded ${i === activeSuggestion ? 'bg-[#E87722]/20 text-[#E87722]' : 'bg-gray-100 text-gray-500'}`} data-id="element-282">
                            {s.field}
                          </span>
                        </button>
                      </li>;
              })}
                </ul>
                <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between text-[10px] text-gray-400" data-id="element-283">
                  <span data-id="element-284">
                    <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-gray-500 font-mono" data-id="element-285">
                      ↑↓
                    </kbd>{' '}
                    Navigieren
                  </span>
                  <span data-id="element-286">
                    <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-gray-500 font-mono" data-id="element-287">
                      ↵
                    </kbd>{' '}
                    Übernehmen
                  </span>
                  <span data-id="element-288">
                    <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-gray-500 font-mono" data-id="element-289">
                      Esc
                    </kbd>{' '}
                    Schließen
                  </span>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        <div className="flex-1" data-id="element-290" />

        {(meta.showStatus || meta.showDates) && <FilterPopover filters={filters} onChange={onChange} activeCount={activeFilterCount} meta={meta} data-id="element-291" />}

        {onOpenTransfer && <button onClick={onOpenTransfer} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-[#E87722] hover:bg-[#d0681b] transition-colors shadow-sm" data-id="element-292">
            <Send className="w-4 h-4" data-id="element-293" />
            Chargen übertragen
            {pendingTransferCount > 0 && <span className="bg-white text-[#E87722] text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-id="element-294">
                {pendingTransferCount}
              </span>}
          </button>}

        {columns.length > 0 && onVisibleColumnsChange && <ColumnsPopover columns={columns} visibleColumns={visibleColumns} onChange={onVisibleColumnsChange} hiddenCount={hiddenColumnsCount} data-id="element-295" />}
      </div>

      {(activeFilterCount > 0 || !allSelected && filters.search) && <div className="w-full flex flex-wrap items-center gap-2 mt-3" data-id="element-296">
          <span className="text-xs text-gray-500 font-medium" data-id="element-297">
            Aktive Filter:
          </span>
          {!allSelected && filters.search && <FilterChip label={`Suche in: ${selectedFieldCount === 1 ? fieldsSummary : selectedFieldCount + ' Felder'}`} onRemove={() => update({
        searchFields: allFields.map(f => f.key)
      })} data-id="element-298" />}
          {filters.status !== 'Alle' && <FilterChip label={`Status: ${filters.status}`} onRemove={() => update({
        status: 'Alle'
      })} data-id="element-299" />}
          {filters.dateFrom && <FilterChip label={`Von: ${formatDate(filters.dateFrom)}`} onRemove={() => update({
        dateFrom: ''
      })} data-id="element-300" />}
          {filters.dateTo && <FilterChip label={`Bis: ${formatDate(filters.dateTo)}`} onRemove={() => update({
        dateTo: ''
      })} data-id="element-301" />}
          <button onClick={() => update({
        status: 'Alle',
        dateFrom: '',
        dateTo: '',
        searchFields: allFields.map(f => f.key)
      })} className="text-xs text-gray-500 hover:text-[#E87722] underline underline-offset-2 ml-auto" data-id="element-302">
            Alle zurücksetzen
          </button>
        </div>}
    </div>;
}
function HighlightMatch({
  text,
  term
}: {
  text: string;
  term: string;
}) {
  if (!term) return <>{text}</>;
  const lower = text.toLowerCase();
  const t = term.toLowerCase();
  const idx = lower.indexOf(t);
  if (idx === -1) return <>{text}</>;
  return <>
      {text.slice(0, idx)}
      <mark className="bg-[#E87722]/20 text-[#E87722] font-semibold rounded-sm px-0.5" data-id="element-303">
        {text.slice(idx, idx + term.length)}
      </mark>
      {text.slice(idx + term.length)}
    </>;
}
function FilterChip({
  label,
  onRemove
}: {
  label: string;
  onRemove: () => void;
}) {
  return <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-[#E87722]/10 text-[#E87722] text-xs font-medium border border-[#E87722]/20" data-id="element-304">
      {label}
      <button onClick={onRemove} className="p-0.5 rounded-full hover:bg-[#E87722]/20" aria-label={`${label} entfernen`} data-id="element-305">
        <X className="w-3 h-3" data-id="element-306" />
      </button>
    </span>;
}
interface FilterPopoverProps {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  activeCount: number;
  meta: (typeof LEVEL_META)[Level];
}
function FilterPopover({
  filters,
  onChange,
  activeCount,
  meta
}: FilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const update = (patch: Partial<FilterState>) => onChange({
    ...filters,
    ...patch
  });
  return <div ref={ref} className="relative" data-id="element-307">
      <button onClick={() => setOpen(o => !o)} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${activeCount > 0 ? 'border-[#E87722] bg-[#E87722]/10 text-[#E87722]' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`} data-id="element-308">
        <SlidersHorizontal className="w-4 h-4" data-id="element-309" />
        Filter
        {activeCount > 0 && <span className="bg-[#E87722] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-id="element-310">
            {activeCount}
          </span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-311" />
      </button>

      <AnimatePresence data-id="element-312">
        {open && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.15
      }} className="absolute right-0 mt-2 w-80 bg-white rounded-xl border border-gray-200 shadow-xl z-30 overflow-hidden" data-id="element-313">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" data-id="element-314">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-315">Filter</h4>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-gray-100 text-gray-400" data-id="element-316">
                <X className="w-4 h-4" data-id="element-317" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-318">
              {meta.showStatus && <div data-id="element-319">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-320">
                    Status
                  </label>
                  <div className="grid grid-cols-2 gap-1.5" data-id="element-321">
                    {(['Alle', 'In Produktion', 'Bereit zum Verpacken', 'Abgeschlossen'] as const).map(s => <button key={s} onClick={() => update({
                status: s
              })} className={`px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${filters.status === s ? 'bg-[#E87722] border-[#E87722] text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`} data-id="element-322">
                        {s}
                      </button>)}
                  </div>
                </div>}

              {meta.showDates && <div data-id="element-323">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-324">
                    Zeitraum
                  </label>
                  <div className="grid grid-cols-2 gap-2" data-id="element-325">
                    <input type="date" value={filters.dateFrom} onChange={e => update({
                dateFrom: e.target.value
              })} className="block w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#E87722] focus:border-[#E87722]" data-id="element-326" />
                    <input type="date" value={filters.dateTo} onChange={e => update({
                dateTo: e.target.value
              })} className="block w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#E87722] focus:border-[#E87722]" data-id="element-327" />
                  </div>
                </div>}
            </div>

            {activeCount > 0 && <div className="px-4 py-3 border-t border-gray-100 bg-gray-50" data-id="element-328">
                <button onClick={() => update({
            status: 'Alle',
            dateFrom: '',
            dateTo: ''
          })} className="text-xs text-gray-600 hover:text-[#E87722] font-medium" data-id="element-329">
                  Filter zurücksetzen
                </button>
              </div>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
interface ColumnsPopoverProps {
  columns: ColumnDef[];
  visibleColumns: string[];
  onChange: (cols: string[]) => void;
  hiddenCount: number;
}
function ColumnsPopover({
  columns,
  visibleColumns,
  onChange,
  hiddenCount
}: ColumnsPopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const toggle = (key: string) => {
    if (visibleColumns.includes(key)) {
      onChange(visibleColumns.filter(k => k !== key));
    } else {
      onChange([...visibleColumns, key]);
    }
  };
  return <div ref={ref} className="relative" data-id="element-330">
      <button onClick={() => setOpen(o => !o)} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${hiddenCount > 0 ? 'border-[#E87722] bg-[#E87722]/10 text-[#E87722]' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`} data-id="element-331">
        <Columns3 className="w-4 h-4" data-id="element-332" />
        Spalten
        {hiddenCount > 0 && <span className="text-[10px] font-bold text-[#E87722]" data-id="element-333">
            {columns.length - hiddenCount}/{columns.length}
          </span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-334" />
      </button>

      <AnimatePresence data-id="element-335">
        {open && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.15
      }} className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-xl z-30 overflow-hidden" data-id="element-336">
            <div className="px-4 py-3 border-b border-gray-100" data-id="element-337">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-338">
                Spalten anzeigen
              </h4>
              <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-339">
                Wählen Sie sichtbare Spalten
              </p>
            </div>

            <div className="py-1 max-h-72 overflow-y-auto" data-id="element-340">
              {columns.map(col => {
            const isVisible = visibleColumns.includes(col.key);
            return <button key={col.key} onClick={() => !col.required && toggle(col.key)} disabled={col.required} className={`w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${col.required ? 'cursor-not-allowed opacity-60' : ''}`} data-id="element-341">
                    <span className="text-gray-700" data-id="element-342">{col.label}</span>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${isVisible ? 'bg-[#E87722] border-[#E87722]' : 'border-gray-300 bg-white'}`} data-id="element-343">
                      {isVisible && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-344" />}
                    </div>
                  </button>;
          })}
            </div>

            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex justify-between" data-id="element-345">
              <button onClick={() => onChange(columns.map(c => c.key))} className="text-xs text-gray-600 hover:text-[#E87722] font-medium" data-id="element-346">
                Alle anzeigen
              </button>
              <button onClick={() => onChange(columns.filter(c => c.required).map(c => c.key))} className="text-xs text-gray-600 hover:text-[#E87722] font-medium" data-id="element-347">
                Nur Pflichtfelder
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function useClickOutside(ref: React.RefObject<HTMLElement>, handler: () => void) {
  useEffect(() => {
    const listener = (e: MouseEvent) => {
      if (!ref.current || ref.current.contains(e.target as Node)) return;
      handler();
    };
    document.addEventListener('mousedown', listener);
    return () => document.removeEventListener('mousedown', listener);
  }, [ref, handler]);
}
function formatDate(iso: string) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}